import classes from "./styles.module.css";
import { Button, CircularProgress, TextField } from "@mui/material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import axios from "axios";
import { path } from "../../../routes";
import { useEffect, useMemo, useState } from "react";

const getDisplayBaseUrl = () => {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/`;
};

const HOLIDAY_INN_ORG_ID = "E15F8010-B7EB-4526-A985-F00D517EBF4C";

const buildDeviceUrl = (base, row) => {
  const params = new URLSearchParams();
  // Prefer what device reports via heartbeat (url_* columns)
  const urlId = row?.url_id ?? row?.urlId;
  const urlRoom = row?.url_room ?? row?.urlRoom;
  const urlOrg = row?.url_orgID ?? row?.urlOrgID;
  const urlKiosk = row?.url_kiosk ?? row?.urlKiosk;
  const orgId = row?.cOrganization_id;
  const normOrg = String(orgId || "").replace(/[{}]/g, "").toUpperCase();
  const isHolidayInn = normOrg === HOLIDAY_INN_ORG_ID;
  // V DB bývá device_group: 1=Displej, 2=Kiosk. Pokud je null/string/legacy, bereme vše kromě "2" jako displej.
  const isDisplay = Number(row?.device_group) !== 2;

  const hasHeartbeatUrl =
    (urlId != null && String(urlId).trim() !== "") ||
    (urlRoom != null && String(urlRoom).trim() !== "") ||
    (urlOrg != null && String(urlOrg).trim() !== "") ||
    (urlKiosk != null && String(urlKiosk).trim() !== "");

  if (hasHeartbeatUrl) {
    // Holiday Inn displeje (meeting view) vždy používat krátkou URL: ?id=11&room=<deviceId>
    // Platí pro všechny meeting roomy (A-F), rozhoduje deviceId (= room param).
    if (isHolidayInn && isDisplay) {
      params.set("id", "11");
      const room = (urlRoom != null && String(urlRoom).trim() !== "") ? String(urlRoom) : String(row.id);
      params.set("room", room);
      if (urlKiosk != null && String(urlKiosk).trim() !== "") params.set("kiosk", String(urlKiosk));
      return `${base}?${params.toString()}`;
    }

    const urlIdStr = urlId != null ? String(urlId).trim() : "";
    if (urlIdStr) params.set("id", urlIdStr);
    if (urlRoom != null && String(urlRoom).trim() !== "") params.set("room", String(urlRoom));
    // Holiday Inn: krátké URL bez orgID
    // - meeting view: ?id=11&room=..
    // - totem A-D/E-F: ?id=12 / ?id=13
    if (!(isHolidayInn && (urlIdStr === "11" || urlIdStr === "12" || urlIdStr === "13"))) {
      if (urlOrg != null && String(urlOrg).trim() !== "") params.set("orgID", String(urlOrg));
    }
    if (urlKiosk != null && String(urlKiosk).trim() !== "") params.set("kiosk", String(urlKiosk));
    return `${base}?${params.toString()}`;
  }

  // Fallback: generate default URL by device type + org convention
  const id = isDisplay ? (isHolidayInn ? "11" : "101") : "100";
  params.set("id", id);
  params.set("room", String(row.id));
  // Holiday Inn displej meeting view: bez orgID (krátká URL)
  if (!(isHolidayInn && id === "11")) {
    if (orgId) params.set("orgID", String(orgId));
  }
  return `${base}?${params.toString()}`;
};

const formatLastSeen = (ts) => {
  if (ts == null || typeof ts !== "number") return "—";
  const d = new Date(ts);
  const now = Date.now();
  const diffMin = Math.floor((now - ts) / 60000);
  if (diffMin < 1) return "právě teď";
  if (diffMin < 60) return `před ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `před ${diffH} h`;
  return d.toLocaleString("cs-CZ", { dateStyle: "short", timeStyle: "short" });
};

const GRID_SERVIS_ALL = "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 80px) 100px 160px minmax(200px, auto)";
const GRID_SERVIS_ORG = "minmax(0, 1fr) minmax(0, 80px) 100px 160px minmax(200px, auto)";

const SortableHeader = ({ column, children }) => (
  <span
    role="button"
    tabIndex={0}
    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && column.toggleSorting(column.getIsSorted() === "asc")}
    style={{ cursor: "pointer", userSelect: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
  >
    {children}
    {column.getIsSorted() === "asc" && " ↑"}
    {column.getIsSorted() === "desc" && " ↓"}
  </span>
);

const Servis = (props) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterName, setFilterName] = useState("");
  const [sorting, setSorting] = useState([{ id: "isAlive", desc: false }]);
  const showAll = !props.org;

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const url = showAll ? `${path}/devicesStatusAll` : `${path}/devicesStatus/${props.org}`;
      const res = await axios.get(url);
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Servis fetch:", e);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [props.org]);

  useEffect(() => {
    const interval = setInterval(fetchStatus, 15000);
    return () => clearInterval(interval);
  }, [props.org]);

  const columns = useMemo(() => {
    const cols = [];
    if (showAll) {
      cols.push({
        id: "organizationName",
        accessorFn: (r) => r.organizationName || r.cOrganization_id || "—",
        header: ({ column }) => (
          <SortableHeader column={column}>Organizace</SortableHeader>
        ),
        enableSorting: true,
      });
    }
    cols.push(
      {
        id: "description",
        accessorFn: (r) => r.description || `#${r.id}`,
        header: ({ column }) => (
          <SortableHeader column={column}>Name</SortableHeader>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) =>
          (rowA.original.description || "").localeCompare(rowB.original.description || "", "cs"),
      },
      {
        id: "device_group",
        accessorFn: (r) => (r.device_group === 1 ? "Displej" : "Kiosk"),
        header: ({ column }) => (
          <SortableHeader column={column}>Type</SortableHeader>
        ),
        enableSorting: true,
      },
      {
        id: "isAlive",
        accessorFn: (r) => (r.isAlive ? 1 : 0),
        header: ({ column }) => (
          <SortableHeader column={column}>Stav</SortableHeader>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) => (rowA.original.isAlive ? 1 : 0) - (rowB.original.isAlive ? 1 : 0),
      },
      {
        id: "lastSeen",
        accessorKey: "lastSeen",
        header: ({ column }) => (
          <SortableHeader column={column}>Naposledy aktivní</SortableHeader>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) => (rowA.original.lastSeen ?? 0) - (rowB.original.lastSeen ?? 0),
      },
      {
        id: "actions",
        header: "Akce",
        enableSorting: false,
        cell: () => null,
      }
    );
    return cols;
  }, [showAll]);

  const table = useReactTable({
    data: list,
    columns,
    state: {
      sorting,
      globalFilter: filterName,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const q = (filterValue || "").trim().toLowerCase();
      if (!q) return true;
      const name = (row.original.description || "").toLowerCase();
      const org = (row.original.organizationName != null ? String(row.original.organizationName) : "").toLowerCase();
      return name.includes(q) || org.includes(q);
    },
  });

  const openDevice = (row) => {
    const base = getDisplayBaseUrl();
    const url = buildDeviceUrl(base, row);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyUrl = async (row) => {
    const base = getDisplayBaseUrl();
    const url = buildDeviceUrl(base, row);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        alert("URL zkopírována do schránky.");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const gridColumns = showAll ? GRID_SERVIS_ALL : GRID_SERVIS_ORG;
  const headerGroup = table.getHeaderGroups()[0];
  const rows = table.getRowModel().rows;
  const colCount = headerGroup?.headers.length ?? 0;

  return (
    <div className={classes.main}>
      <div className={classes.buttons}>
        <Button onClick={fetchStatus} disabled={loading} color="primary" variant="outlined">
          Obnovit
        </Button>
        <TextField
          size="small"
          placeholder="Filtrovat podle názvu..."
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          sx={{ minWidth: 220 }}
          inputProps={{ "aria-label": "Filtrovat podle názvu" }}
        />
      </div>
      <div className={classes.table}>
        <div
          className={classes.tableGridWrapper}
          style={{ gridTemplateColumns: gridColumns }}
        >
          <div style={{ display: "contents" }}>
            {headerGroup?.headers.map((header, i) => (
              <div
                key={header.id}
                className={`${classes.cellHeader} ${i === 0 ? classes.cellColFirst : ""} ${i === colCount - 1 ? classes.cellColLast : ""} ${header.column.id === "actions" ? classes.columnAct : classes.column}`}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </div>
            ))}
          </div>
          {loading && (
            <div className={classes.loader} style={{ gridColumn: "1 / -1" }}>
              <CircularProgress size={40} />
            </div>
          )}
          {!loading && list.length === 0 && (
            <div className={classes.empty} style={{ gridColumn: "1 / -1" }}>
              Žádné terminály
            </div>
          )}
          {!loading && list.length > 0 && rows.length === 0 && (
            <div className={classes.empty} style={{ gridColumn: "1 / -1" }}>
              Žádný výsledek pro „{filterName.trim()}“
            </div>
          )}
          {!loading &&
            rows.map((row, rowIndex) => (
              <div key={row.id} style={{ display: "contents" }}>
                {row.getVisibleCells().map((cell, i) => {
                  const isFirst = i === 0;
                  const isLast = i === colCount - 1;
                  const isLastRow = rowIndex === rows.length - 1;
                  const cellClasses = `${classes.cellData} ${isFirst ? classes.cellColFirst : ""} ${isLast ? classes.cellColLast : ""} ${isLastRow ? classes.cellRowLast : ""} ${cell.column.id === "actions" ? classes.columnAct : classes.column}`;
                  if (cell.column.id === "actions") {
                    return (
                      <div key={cell.id} className={cellClasses}>
                        <Button size="small" onClick={() => openDevice(row.original)} variant="outlined" color="primary">
                          Otevřít
                        </Button>
                        <Button size="small" onClick={() => copyUrl(row.original)} variant="outlined" color="primary">
                          URL
                        </Button>
                      </div>
                    );
                  }
                  let content = flexRender(cell.column.columnDef.cell, cell.getContext());
                  if (cell.column.id === "isAlive" && cell.getValue() !== undefined) {
                    content = row.original.isAlive ? (
                      <span style={{ color: "var(--success, #2e7d32)" }}>Běží</span>
                    ) : (
                      <span style={{ color: "var(--error, #c62828)" }}>Neaktivní</span>
                    );
                  }
                  if (cell.column.id === "lastSeen") {
                    content = formatLastSeen(row.original.lastSeen);
                  }
                  return (
                    <div key={cell.id} className={cellClasses}>
                      {content}
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Servis;
